import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import { type RouterInputs, trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/ui/icons";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { usePrintLabel } from "@/lib/labels";
import type { TAccessionDetail, TAccessionStatus, TSample } from "@/lib/types";
import { useDialogProps } from "../detail/state";
import { ReceiveFormProvider, useReceiveForm } from "../ReceiveFormProvider";
import { ShipperValidationForm } from "../ShipperValidationForm";

export function ReceiveDialog({ accession }: { accession: TAccessionDetail }) {
  const dialogProps = useDialogProps("receive");
  const { printSpecimenLabels } = usePrintLabel();
  const { data: samples } = trpc.accession.samples.useQuery(accession.id, {
    enabled: dialogProps.open,
  });

  const receive = trpc.accession.receive.useMutation({
    async onSuccess() {
      toast.success("Accession received");

      await printSpecimenLabels(accession, samples ?? []);

      dialogProps.onOpenChange?.(false);
    },
    onError(err) {
      toast.error("Error receiving accession", {
        description: err.message,
      });
    },
  });

  const callReceive = useCallback(
    (variables: Omit<RouterInputs["accession"]["receive"], "id">) => {
      receive.mutate({ id: accession.id, ...variables });
    },
    [accession.id, receive],
  );

  return (
    <Modal isOpen={dialogProps.open} onOpenChange={dialogProps.onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Receive accession</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text>
            Please verify the shipment information below and click receive to
            complete the accession receipt process.
          </Text>
          <ReceiveFormProvider>
            <ReceiveForm
              accession={accession}
              samples={samples ?? []}
              onSubmit={callReceive}
            />
          </ReceiveFormProvider>
        </DialogBody>
        <DialogFooter className="flex flex-row-reverse">
          <Button
            type="submit"
            form="receive-form"
            isPending={receive.isPending}
            isDisabled={receive.isPending}
          >
            Receive
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

// biome-ignore lint/suspicious/noExplicitAny: TODO
type ReceiveFormValues = any;

function ReceiveForm({
  accession,
  samples,
  onSubmit,
}: {
  accession: TAccessionDetail;
  samples: TSample[];
  onSubmit: (values: ReceiveFormValues) => void;
}) {
  const { schema, convert } = useReceiveForm();
  const methods = useForm<ReceiveFormValues>({
    resolver: zodResolver(schema),
  });

  const convertAndSubmit: SubmitHandler<ReceiveFormValues> = useCallback(
    (values) => {
      const converted = convert(values);
      onSubmit(converted);
    },
    [convert, onSubmit],
  );

  return (
    <form id="receive-form" onSubmit={methods.handleSubmit(convertAndSubmit)}>
      <input
        type="hidden"
        {...methods.register("id", { valueAsNumber: true })}
        value={accession.id}
      />
      <FormProvider {...methods}>
        <ShipperValidationForm accession={accession} samples={samples} />
      </FormProvider>
    </form>
  );
}

export function UnreceiveDialog({
  accession,
}: {
  accession: { id: number; status: TAccessionStatus };
}) {
  const dialogProps = useDialogProps("unreceive");

  const unreceive = trpc.accession.unreceive.useMutation({
    async onSuccess() {
      toast.success("Accession not received");
      dialogProps.onOpenChange?.(false);
    },
    onError(err) {
      toast.error("Error reverting receive", {
        description: err.message,
      });
    },
  });

  const callUnreceive = useCallback(async () => {
    unreceive.mutate({ id: accession.id });
  }, [accession.id, unreceive]);

  return (
    <Modal isOpen={dialogProps.open} onOpenChange={dialogProps.onOpenChange}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Revert accession receipt</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {accession.status === "received" ? (
            <Text>
              Are you sure you want to revert receipt of this accession?
            </Text>
          ) : (
            <Text>
              Cannot revert receipt of accession in status{" "}
              <StatusBadge status={accession.status} />
            </Text>
          )}
        </DialogBody>
        <DialogFooter>
          {accession.status === "received" && (
            <Button
              isPending={unreceive.isPending}
              isDisabled={unreceive.isPending}
              onPress={callUnreceive}
            >
              Revert
            </Button>
          )}
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
